import { create } from 'zustand';
import api from '../services/api';
import { useAuthStore } from './authStore';

export const useChatStore = create((set, get) => ({
  channels: [],
  currentChannel: null,
  messages: [],
  onlineUsers: [],
  typingUsers: {},
  // Split so a background channel-list refresh (e.g. bumping unread counts
  // on every incoming socket message) can't flip the open chat back into
  // its loading/skeleton state — only the per-channel message fetch should.
  channelsLoading: false,
  messagesLoading: false,
  sentMessageIds: new Set(), // Track messages sent by current user to prevent duplicates
  // channelId -> { active, callType, participants[] } for calls in progress
  activeCalls: {},

  setCallStatus: (status) => {
    set((state) => {
      const activeCalls = { ...state.activeCalls };
      if (status.active) activeCalls[status.channelId] = status;
      else delete activeCalls[status.channelId];
      return { activeCalls };
    });
  },

  fetchChannels: async () => {
    set({ channelsLoading: true });
    try {
      const response = await api.get('/channels');
      set({ channels: response.data.channels, channelsLoading: false });
    } catch (error) {
      console.error('Error fetching channels:', error);
      set({ channelsLoading: false });
    }
  },

  setCurrentChannel: async (channel) => {
    set({ currentChannel: channel, messages: [], messagesLoading: true });
    try {
      const response = await api.get(`/messages/${channel._id}`);
      set({ messages: response.data.messages, messagesLoading: false });
    } catch (error) {
      console.error('Error fetching messages:', error);
      set({ messagesLoading: false });
    }
  },

  // WhatsApp-style send: the message shows up instantly as "pending" and is swapped for the
  // saved one when the server answers, so several messages can be fired off back to back.
  // `replyToMessage` is the full message being replied to (for the preview while pending).
  sendMessage: async (channelId, content, type = 'text', mentions = [], replyTo = null, { replyToMessage = null } = {}) => {
    const user = useAuthStore.getState().user;
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const tempMessage = {
      _id: tempId,
      clientTempId: tempId,
      channel: channelId,
      sender: user ? { ...user, _id: user._id || user.id } : null,
      content,
      type,
      mentions,
      replyTo: replyToMessage,
      attachments: [],
      createdAt: new Date().toISOString(),
      pending: true
    };

    const inThisChannel = () => String(get().currentChannel?._id || '') === String(channelId);
    if (inThisChannel()) {
      set((state) => ({ messages: [...state.messages, tempMessage] }));
    }

    try {
      const payload = { channel: channelId, content, type, mentions };
      if (replyTo) payload.replyTo = replyTo;

      const response = await api.post('/messages', payload);
      const newMessage = response.data.message;

      if (newMessage) {
        // Remember it so the socket echo of our own message isn't added twice
        get().sentMessageIds.add(newMessage._id);
        setTimeout(() => get().sentMessageIds.delete(newMessage._id), 5000);

        set((state) => {
          // The socket echo may already have replaced the pending copy
          const alreadyIn = state.messages.some(m => m._id === newMessage._id);
          return {
            messages: alreadyIn
              ? state.messages.filter(m => m._id !== tempId)
              : state.messages.map(m => (m._id === tempId ? { ...newMessage, clientTempId: tempId } : m))
          };
        });
      }
      return newMessage;
    } catch (error) {
      console.error('Error sending message:', error);
      set((state) => ({
        messages: state.messages.map(m => (m._id === tempId ? { ...m, pending: false, failed: true } : m))
      }));
      return null;
    }
  },

  // Re-send a message that failed (tap on "Not sent")
  retryMessage: (tempId) => {
    const failed = get().messages.find(m => m._id === tempId && m.failed);
    if (!failed) return;
    set((state) => ({ messages: state.messages.filter(m => m._id !== tempId) }));
    get().sendMessage(
      String(failed.channel?._id || failed.channel),
      failed.content,
      failed.type,
      failed.mentions,
      failed.replyTo?._id || null,
      { replyToMessage: failed.replyTo }
    );
  },

  addMessage: (message) => {
    const currentChannel = get().currentChannel;
    if (currentChannel) {
      // Convert both to strings for reliable comparison
      const messageChannelId = String(message.channel?._id || message.channel || '');
      const currentChannelId = String(currentChannel._id || '');
      
      console.log('addMessage check:', {
        messageChannelId,
        currentChannelId,
        matches: messageChannelId === currentChannelId,
        messageId: message._id,
        message
      });
      
      if (messageChannelId === currentChannelId && messageChannelId !== '') {
        // ALWAYS check if message already exists first (most important check)
        const messageExists = get().messages.some(m => m._id === message._id);
        if (messageExists) {
          console.log('Message already exists in state, skipping duplicate:', message._id);
          return;
        }
        
        // Check if this message was already added by sender (optimistic update)
        const sentMessages = get().sentMessageIds || new Set();
        if (sentMessages.has(message._id)) {
          console.log('Message already added optimistically, skipping socket duplicate');
          // Remove from sentMessages set to free memory
          sentMessages.delete(message._id);
          set({ sentMessageIds: sentMessages });
          return;
        }

        // Our own message echoed back before the POST answered: swap it into the pending copy's place
        const me = useAuthStore.getState().user;
        const senderId = String(message.sender?._id || message.sender || '');
        if (me && senderId === String(me._id || me.id)) {
          const pending = get().messages.find(m => m.pending && m.content === message.content);
          if (pending) {
            set((state) => ({
              messages: state.messages.map(m => (m === pending ? { ...message, clientTempId: pending.clientTempId } : m))
            }));
            return;
          }
        }
        
        console.log('Adding message to state:', message._id);
        set((state) => ({
          messages: [...state.messages, message]
        }));
      }
      // Note: Dashboard.jsx now handles refreshing channels for unread count
    }
  },

  updateMessage: (updatedMessage) => {
    set((state) => ({
      messages: state.messages.map(msg => 
        msg._id === updatedMessage._id ? updatedMessage : msg
      )
    }));
  },

  deleteMessage: (messageId) => {
    set((state) => ({
      messages: state.messages.filter(msg => msg._id !== messageId)
    }));
  },

  setOnlineUsers: (users) => {
    set({ onlineUsers: users });
  },

  updateUserStatus: (userId, status) => {
    set((state) => ({
      onlineUsers: status === 'online' 
        ? [...new Set([...state.onlineUsers, userId])]
        : state.onlineUsers.filter(id => id !== userId)
    }));
  },

  setTypingUser: (channelId, userId, username) => {
    set((state) => ({
      typingUsers: {
        ...state.typingUsers,
        [channelId]: { userId, username }
      }
    }));

    // Clear typing after 3 seconds
    setTimeout(() => {
      set((state) => {
        const newTypingUsers = { ...state.typingUsers };
        delete newTypingUsers[channelId];
        return { typingUsers: newTypingUsers };
      });
    }, 3000);
  },

  clearTypingUser: (channelId) => {
    set((state) => {
      const newTypingUsers = { ...state.typingUsers };
      delete newTypingUsers[channelId];
      return { typingUsers: newTypingUsers };
    });
  },

  addChannel: (channel) => {
    set((state) => ({
      channels: [...state.channels, channel]
    }));
  },

  removeChannel: (channelId) => {
    set((state) => {
      // Filter out the deleted channel
      const updatedChannels = state.channels.filter(c => c._id !== channelId);
      
      // If current channel was deleted, clear it
      const newCurrentChannel = state.currentChannel?._id === channelId ? null : state.currentChannel;
      
      return {
        channels: updatedChannels,
        currentChannel: newCurrentChannel,
        messages: newCurrentChannel ? state.messages : []
      };
    });
  }
}));
